/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
import './style.scss';

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { Message } from 'primereact/message';
import ReactRouterPrompt from 'react-router-prompt';

import { getGeneralInfoById } from 'apis/projectProposals.api';

import {
  createForMultipleProjectProposals as createCouncil,
  getAllCouncilMemberRoles,
  getAllCouncilTypes,
  downloadFile,
} from 'apis/councils.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';
import { Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import { DatePicker, FileInput, Select, TextInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { COUNCIL_TYPE_CODE, TOAST } from 'constant';
import MemberDialog from 'features/Council/components/AddMemberDialog';
import ConfirmSendMailMultipleProJectDialog from 'features/Council/components/ConfirmSendMailMultipleProjectDialog';
import { createCouncilSchema } from 'features/Council/validation';
import { toast } from 'layout';
import {
  getAcadamicLevelShortHand,
  getContactOrganizationName,
  getEndingTimeOfDate,
  getNameRoleOfMemberInCouncil,
  getOrganizationNameById,
  formatFileName,
  getCodeCouncilTypeById,
} from 'utils/func';
import AddProjectProposalDialog from 'features/Council/components/AddProjectProposalDialog';
import DateTimePicker from 'components/FormControl/DateTimePicker';
import ConfirmCloseCouncilDialog from 'features/Council/components/ConfirmCloseCouncilDialog';

const formOptions = { resolver: yupResolver(createCouncilSchema) };
export default function CreateForManyProjectProposal() {
  // #region Data
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [projectProposalIdFromLocation, setProjectProposalIdFromLocation] = useState(null);
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleAddProjectProposal, setIsVisibleAddProjectProposal] = useState(false);
  const [isVisibleConfirmDoneDialog, setIsVisibleConfirmDoneDialog] = useState(false);
  const [isSentMail, setIsSentMail] = useState(false);
  const [proposalMembers, setProposalMembers] = useState([]);
  const [decisionToEstablishFileUrl, setDecisionToEstablishFileUrl] = useState(null);
  const [councilTypeSelected, setCouncilTypeSelected] = useState({});
  const [isConfirmResetProjectProposal, setIsConfirmResetProjectProposal] = useState(false);
  const [isBack, setIsBack] = useState(false);
  const [isCloseCouncil, setIsCloseCouncil] = useState([false, true]);

  const { data: projectProposalData } = useQuery({
    queryKey: ['generalInfoProjectProposal'],
    queryFn: () => getGeneralInfoById(projectProposalIdFromLocation),
    enabled: !!projectProposalIdFromLocation,
  });

  const projectProposalFromLocation = useMemo(
    () => projectProposalData?.data ?? null,
    [projectProposalData]
  );

  const { data: councilMemberRolesData } = useQuery(
    ['councilMemberRoles'],
    getAllCouncilMemberRoles,
    {
      staleTime: 5000 * 60,
    }
  );

  const roles = useMemo(() => {
    if (councilMemberRolesData?.data?.length > 0) {
      return councilMemberRolesData.data.map((councilMemberRole) => ({
        value: councilMemberRole.id,
        name: t(`council.role.${councilMemberRole.code}`),
      }));
    }
    return [];
  }, [councilMemberRolesData?.data, t]);

  const { data: councilTypesData, isLoading: isTypeCouncilLoading } = useQuery(
    ['councilTypes'],
    getAllCouncilTypes,
    {
      staleTime: 5000 * 60,
    }
  );
  const councilTypes = useMemo(() => councilTypesData?.data ?? [], [councilTypesData]);

  const typeCouncils = useMemo(() => {
    if (!councilTypes || councilTypes?.length === 0) {
      return [];
    }

    const filteredCouncilTypes = councilTypes
      .filter(({ code }) => code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL)
      .map(({ id, code }) => ({
        value: id,
        name: `${t(`council.viewList.${code}`)}`,
        code,
        id,
      }));
    return filteredCouncilTypes;
  }, [councilTypesData?.data, t]);

  const { data: rawOrganizations } = useQuery(['Organizations'], getAllOrganizations, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      rawOrganizations?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [rawOrganizations, t]
  );

  const [council, setCouncil] = useState(null);
  const MemberRef = useRef();
  const AddProjectProposalRef = useRef();
  const {
    mutate,
    error: errorRes,
    isError: isErrorRes,
    isLoading: isLoadingCreate,
  } = useMutation(createCouncil);
  const formDefaultValues = {
    name: '',
    establishDate: '',
    membersFields: [],
    projectProposalIds: [],
  };
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', defaultValues: formDefaultValues, ...formOptions });

  const {
    fields: membersFields,
    append: appendMembers,
    remove: removeMembers,
    update: updateMembers,
  } = useFieldArray({
    control,
    name: 'councilMembers',
  });

  const {
    fields: projectProposalsFields,
    append: appendProjectProposals,
    remove: removeProjectProposals,
  } = useFieldArray({
    control,
    name: 'councilProjectProposals',
    keyName: 'customId',
  });

  const { isDirty, dirtyFields } = useFormState({
    control,
  });

  const getPageMargins = () => '@page { margin: 0.5in 0.5in 0.2in 0.2in  !important; }';

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const handleDownloadFile = (path, id) => {
    mutateDownloadFile(
      { path, id },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };
  // #endregion
  // #region Events
  const onSubmit = async (data) => {
    const formData = new FormData();

    // check member
    if (membersFields.length === 0) {
      toast(TOAST.ERROR, t('errorMessage.councilMustHaveAtLeastOneMember'));
      return;
    }

    // check project proposal
    if (projectProposalsFields.length === 0) {
      toast(TOAST.ERROR, t('errorMessage.councilMustHaveAtLeastOneProjectProposal'));
      return;
    }

    if (data?.decisionToEstablish) {
      formData.append('establishmentFile', data.decisionToEstablish);
    }
    if (data?.establishDate) {
      formData.append('timeCreated', getEndingTimeOfDate(new Date(data.establishDate)));
    }
    if (data?.reviewDeadline) {
      formData.append('reviewDeadline', getEndingTimeOfDate(new Date(data.reviewDeadline)));
    }
    if (data?.timeMet) {
      formData.append('timeMet', new Date(data.timeMet));
    }
    if (data?.meetingLocation) {
      formData.append('meetingLocation', data.meetingLocation);
    }
    if (data?.contact) {
      formData.append('contact', data.contact);
    }
    if (data?.documentUrl) {
      formData.append('documentUrl', data.documentUrl);
    }
    formData.append('name', data.name);
    formData.append('members', JSON.stringify(membersFields));
    const projectProposalIds = data?.councilProjectProposals?.map((item) => item?.id);
    // if (projectProposalIds.length === 0) {
    //   toast(TOAST.ERROR, "")
    // }
    formData.append('projectProposalIds', projectProposalIds);
    formData.append('councilTypeId', data?.councilTypeId);

    mutate(formData, {
      onSuccess: (res) => {
        reset({}, { keepValues: true });
        toast(TOAST.SUCCESS, t('council.create.success'));
        setCouncil(res?.data?.council);
        setDecisionToEstablishFileUrl(res?.data?.establishmentFileUrl);
      },
      onError: (res) => {
        toast(
          TOAST.ERROR,
          t(`errorMessage.${res?.response?.data?.code}`).startsWith('errorMessage')
            ? t('council.create.error')
            : t(`errorMessage.${res?.response?.data?.code}`)
        );
      },
    });
  };

  const councilTypeId = watch('councilTypeId');

  useEffect(() => {
    if (
      councilTypeId &&
      councilTypeSelected?.id &&
      councilTypeId !== councilTypeSelected &&
      !isBack &&
      projectProposalsFields.length > 0
    ) {
      setIsConfirmResetProjectProposal(true);
    } else {
      setIsBack(false);
      setCouncilTypeSelected(typeCouncils.find((item) => item.id === councilTypeId));
    }
  }, [councilTypeId]);

  useEffect(() => {
    if (projectProposalsFields.length > 0) {
      const membersOfProposal = [];
      projectProposalsFields.forEach((pp) => {
        membersOfProposal.push(...pp.projectProposalContacts);
      });
      setProposalMembers([...membersOfProposal]);
    } else setProposalMembers([]);
  }, [projectProposalsFields]);

  useEffect(() => {
    if (location?.state?.projectProposalId) {
      setProjectProposalIdFromLocation(location.state.projectProposalId);
    }
  }, [location]);

  useEffect(() => {
    setValue(
      'councilTypeId',
      councilTypes.find((item) => item.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL)?.id
    );

    if (projectProposalFromLocation && location?.state?.projectProposalId) {
      setValue('councilProjectProposals', [
        {
          vietnameseName: projectProposalFromLocation?.vietnameseName,
          projectType: projectProposalFromLocation?.projectType,
          fullName: projectProposalFromLocation?.projectProposalContacts?.[0]?.fullname,
          program: projectProposalFromLocation?.program?.name,
          programId: projectProposalFromLocation?.program?.id,
          researchFields: projectProposalFromLocation?.projectProposalResearchFields,
          id: projectProposalFromLocation?.id,
          reference: projectProposalFromLocation?.reference,
          projectProposalContacts: projectProposalFromLocation?.projectProposalContacts,
          hasCouncilResult: false,
          hasReview: false,
        },
      ]);
    }
  }, [projectProposalFromLocation, councilTypes, location]);

  const footerConfirmationResetProjectProposal = () => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => {
          setValue('councilTypeId', councilTypeSelected.id);
          setIsBack(true);
          setIsConfirmResetProjectProposal(false);
        }}
        autoFocus
      />
      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          for (let i = projectProposalsFields.length - 1; i >= 0; i -= 1) {
            removeProjectProposals(i);
          }
          setCouncilTypeSelected(typeCouncils.find((item) => item.id === councilTypeId));
          setIsConfirmResetProjectProposal(false);
        }}
      />
    </div>
  );

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const handleCloseCouncil = () => {
    if (council && !isSentMail) {
      setIsCloseCouncil([true, false]);
    } else {
      navigate(-1);
    }
  };
  // #endregion

  return (
    <>
      {(isTypeCouncilLoading || isLoadingCreate) && <Loading />}
      {isErrorRes && (
        <ErrorDialog
          title={t(
            `council.create.titleCreateError.${getCodeCouncilTypeById(typeCouncils, councilTypeId)}`
          )}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <ConfirmSendMailMultipleProJectDialog
        visible={isVisibleConfirmDoneDialog}
        setVisible={setIsVisibleConfirmDoneDialog}
        setIsSentMail={setIsSentMail}
        council={council}
      />
      <ReactRouterPrompt
        when={
          (isDirty && Object.keys(dirtyFields).length && !isVisibleAddProjectProposal) ||
          (council && !isSentMail && isCloseCouncil[1])
        }
      >
        {({ isActive, onConfirm, onCancel }) => (
          <>
            {council && !isSentMail ? (
              <ConfirmCloseCouncilDialog
                visible={isActive}
                setVisible={setIsCloseCouncil}
                onConfirm={onConfirm}
                onHide={onCancel}
              />
            ) : (
              <Dialog
                visible={isActive}
                header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
                position="center"
                style={{ width: '300px' }}
                onHide={onCancel}
                draggable={false}
                footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
              >
                <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
              </Dialog>
            )}
          </>
        )}
      </ReactRouterPrompt>
      <div className="card relative create-council-for-many-projectproposal">
        <style>{getPageMargins()}</style>
        <div className="flex">
          <h3 className="mb-3 line-height-3 text-4xl text-center font-bold flex-1">
            {t('council.createForManyProjectProposal.titleContent')}
          </h3>
        </div>

        <form className="form-create-council ml-5" onSubmit={handleSubmit(onSubmit)}>
          <h4 className="mt-4">{t('council.createForManyProjectProposal.infoCouncil.label')}</h4>
          <div className="relative p-fluid">
            <div>
              <TextInput
                label={t('council.createForManyProjectProposal.infoCouncil.name')}
                isRequired
                name="name"
                isFontWeightLabel
                control={control}
                errors={errors}
                disabled={!!council}
              />
            </div>
            <div className="flex flex-wrap gap-6 ">
              <div className="w-3">
                <Select
                  label={t('council.createForManyProjectProposal.infoCouncil.councilType')}
                  name="councilTypeId"
                  isFontWeightLabel
                  isRequired
                  control={control}
                  errors={errors}
                  options={typeCouncils}
                  disabled={!!council}
                  removable={false}
                />
              </div>
              <div className="w-3">
                <DatePicker
                  label={t('council.createForManyProjectProposal.infoCouncil.establishDate')}
                  isRequired
                  name="establishDate"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
              <div className="w-3">
                <DatePicker
                  label={t('council.createForManyProjectProposal.infoCouncil.reviewDeadline')}
                  name="reviewDeadline"
                  isRequired
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-3">
                <DateTimePicker
                  label={t('council.createForManyProjectProposal.infoCouncil.timeMet')}
                  name="timeMet"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.meetingLocation')}
                  name="meetingLocation"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.contact')}
                  name="contact"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.documentUrl')}
                  name="documentUrl"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div>
              <FileInput
                label={t('council.createForManyProjectProposal.infoCouncil.decisionToEstablish')}
                isFontWeightLabel
                name="decisionToEstablish"
                accept=".pdf"
                acceptOnly="pdfOnly"
                isRequired
                control={control}
                errors={errors}
                disabled={!!council}
              />
              {decisionToEstablishFileUrl && (
                <span
                  className="p-button p-button-text p-button-sm mb-3"
                  onClick={() => handleDownloadFile(decisionToEstablishFileUrl, council?.id)}
                >
                  <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                  <span className="text-blue-500 underline font-italic text-base">
                    {formatFileName(decisionToEstablishFileUrl)}
                  </span>
                </span>
              )}
            </div>
          </div>
          <h4 className="mt-5 mb-3">
            B. {t('council.createForManyProjectProposal.listMemberCouncil.label')}
          </h4>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '30%' }}>
                    {`${t('council.member.fields.academicRank')}, ${t(
                      'council.member.fields.academicDegree'
                    )}, ${t('council.member.fields.name')}`}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    Email
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    {t('council.member.fields.organization')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    {t('council.member.fields.role')}
                  </th>

                  {!council && (
                    <th className="float text-center" style={{ minWidth: 80, width: 80 }}>
                      {t('council.member.fields.action')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className="font-bold">
                  {!council && (
                    <td colSpan={6}>
                      <div>
                        <span>{t('council.create.add')}</span>
                        <i
                          onClick={() => {
                            MemberRef.current.open({
                              append: appendMembers,
                              type: 'APPEND',
                            });
                          }}
                          className="add-scientific-secretary pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle ml-1"
                          data-pr-tooltip={t('formLayout.action.add')}
                          data-pr-position="left"
                        />
                        <Tooltip target=".add-scientific-secretary" />
                      </div>
                    </td>
                  )}
                </tr>
                {membersFields.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td>
                      {`${getAcadamicLevelShortHand(
                        item?.academicRank,
                        item?.academicDegree,
                        t,
                        true
                      )}${item?.fullname}`}
                    </td>
                    <td>{item?.email}</td>
                    <td>
                      {typeof value === 'object'
                        ? getContactOrganizationName(organizations, item?.contact, t)
                        : getOrganizationNameById(
                            item?.organizationId,
                            organizations,
                            item?.specificOrganizationName,
                            t
                          )}
                    </td>
                    <td>{getNameRoleOfMemberInCouncil(roles, item?.roleId)}</td>
                    {!council && (
                      <td className="flex flex-column float text-center p-0 border-none border-bottom-1">
                        <i
                          onClick={() => {
                            MemberRef.current.open({
                              update: updateMembers,
                              index,
                              value: item,
                              type: 'UPDATE',
                            });
                          }}
                          className="edit-icon pi pi-pencil bg-red text-primary cursor-pointer my-2"
                          data-pr-tooltip={t('formLayout.action.edit')}
                          data-pr-position="left"
                        />
                        <Tooltip target=".edit-icon" />

                        <i
                          onClick={() => removeMembers(index)}
                          className="remove-icon pi pi-times text-red-700 cursor-pointer mb-2"
                          data-pr-tooltip={t('formLayout.action.remove')}
                          data-pr-position="left"
                        />
                        <Tooltip target=".remove-icon" />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h4 className="mt-5 mb-3">
            C. {t('council.createForManyProjectProposal.listProjectProposalOfCouncil.label')}
          </h4>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 260, width: '40%' }}>
                    {t(
                      'council.createForManyProjectProposal.listProjectProposalOfCouncil.vietnameseName'
                    )}
                  </th>
                  <th className="text-center" style={{ minWidth: 140, width: '15%' }}>
                    {t('council.createForManyProjectProposal.listProjectProposalOfCouncil.owner')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '20%' }}>
                    {t(
                      'council.createForManyProjectProposal.listProjectProposalOfCouncil.researchField'
                    )}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '20%' }}>
                    {t('council.createForManyProjectProposal.listProjectProposalOfCouncil.program')}
                  </th>

                  {!council && (
                    <th className="float text-center" style={{ minWidth: 80, width: 80 }}>
                      {t('council.member.fields.action')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className="font-bold">
                  {!council && (
                    <td colSpan={6}>
                      <div>
                        <span>
                          {projectProposalsFields.length === 0
                            ? t(
                                'council.createForManyProjectProposal.listProjectProposalOfCouncil.add'
                              )
                            : t(
                                'council.createForManyProjectProposal.listProjectProposalOfCouncil.update'
                              )}
                        </span>
                        <i
                          onClick={() => {
                            if (councilTypeId) {
                              AddProjectProposalRef.current.open({
                                append: appendProjectProposals,
                                fields: projectProposalsFields,
                                type: projectProposalsFields.length === 0 ? 'APPEND' : 'UPDATE',
                                remove: removeProjectProposals,
                              });
                            } else {
                              toast(TOAST.WARN, t('errorMessage.councilTypeIsRequired'));
                            }
                          }}
                          className={
                            projectProposalsFields.length === 0
                              ? 'add-scientific-secretary pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle ml-1'
                              : 'add-scientific-secretary pi pi-pencil text-primary bg-primary cursor-pointer p-1 border-circle ml-1'
                          }
                          data-pr-tooltip={
                            projectProposalsFields.length === 0
                              ? t('formLayout.action.add')
                              : t('formLayout.action.update')
                          }
                          data-pr-position="left"
                        />
                        <Tooltip target=".add-scientific-secretary" />
                      </div>
                    </td>
                  )}
                </tr>
                {projectProposalsFields.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item?.vietnameseName}</td>
                    <td>{item?.fullName}</td>
                    <td>
                      {item?.researchFields
                        ?.sort((a, b) => {
                          if (a.priority === null) return 1;
                          if (b.priority === null) return -1;
                          if (a.priority === b) return 0;
                          return a.priority < b.priority ? -1 : 1;
                        })
                        ?.map((researchField, index2) => (
                          <p key={researchField.researchField.id} className="text-left m-0">
                            {`(${researchField?.priority ?? index2 + 1}) ${t(
                              `researchFields.${researchField.researchField.code}`
                            )}`}
                          </p>
                        ))}
                    </td>
                    <td>{item?.program}</td>
                    {!council && (
                      <td className="float text-center p-0 border-none border-bottom-1 align-center">
                        <i
                          onClick={() => removeProjectProposals(index)}
                          className="remove-icon pi pi-times text-red-700 cursor-pointer mb-2"
                          data-pr-tooltip={t('formLayout.action.remove')}
                          data-pr-position="left"
                        />
                        <Tooltip target=".remove-icon" />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap justify-content-end mt-8">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              onClick={handleCloseCouncil}
              className="mr-2 w-8rem"
            />
            <Button
              label={t('formLayout.action.save')}
              severity="info"
              type="submit"
              onClick={checkValidation}
              disabled={!(isDirty && Object.keys(dirtyFields).length)}
              className="mr-2 w-8rem"
            />
            <Can do="send_mail" on="council">
              <Button
                label={
                  council?.completedAt
                    ? council?.isSentMailAllMembers
                      ? t('formLayout.action.doneAndSendMail')
                      : t('formLayout.action.sentEmailToMembers')
                    : t('formLayout.action.doneAndSendMail')
                }
                severity="success"
                type="button"
                onClick={() => setIsVisibleConfirmDoneDialog(true)}
                disabled={(!council && !isSentMail) || (council && isSentMail)}
                className="mr-2 w-auto"
              />
            </Can>
          </div>
          <MemberDialog
            ref={MemberRef}
            organizations={organizations}
            curMembers={membersFields}
            roles={roles}
            projectProposalMembers={proposalMembers}
            councilTypeSelected={councilTypeSelected?.code}
          />

          <AddProjectProposalDialog
            ref={AddProjectProposalRef}
            curMembers={membersFields}
            councilTypeSelected={councilTypeSelected}
            isVisible={isVisibleAddProjectProposal}
            setIsVisible={setIsVisibleAddProjectProposal}
          />

          <ConfirmCloseCouncilDialog visible={isCloseCouncil[0]} setVisible={setIsCloseCouncil} />

          <Dialog
            header={<p className="text-blue-500">{t('formLayout.action.confirm')}</p>}
            visible={isConfirmResetProjectProposal}
            position="center"
            style={{ width: '400px' }}
            onHide={() => {
              setIsBack(true);
              setValue('councilTypeId', councilTypeSelected.id);
              setIsConfirmResetProjectProposal(false);
            }}
            draggable={false}
            footer={footerConfirmationResetProjectProposal}
          >
            <Message
              className="mt-4 w-full"
              severity="warn"
              text={t('council.createForManyProjectProposal.messageConfirmation')}
            />
          </Dialog>
        </form>
      </div>
    </>
  );
}
